import ExcelJS from "exceljs";
import { dash, formatDate, formatMoney, readableType } from "./downlineViewShared";

const createWorkbook = () => {
  const root = ExcelJS?.Workbook ? ExcelJS : ExcelJS?.default;
  if (!root?.Workbook) throw new Error("Excel export is unavailable");
  return new root.Workbook();
};

const WHITE = "FFFFFFFF";
const INK = "FF0F172A";
const TEAL = "FF0F766E";
const TEAL_DARK = "FF115E59";
const MINT = "FFCCFBF1";
const MINT_SOFT = "FFF0FDFA";
const SLATE = "FF475569";
const ZEBRA = "FFF8FAFC";
const LINE = "FFE2E8F0";

const solid = (argb) => ({ type: "pattern", pattern: "solid", fgColor: { argb } });
const border = {
  top: { style: "thin", color: { argb: LINE } },
  left: { style: "thin", color: { argb: LINE } },
  bottom: { style: "thin", color: { argb: LINE } },
  right: { style: "thin", color: { argb: LINE } },
};

const moneyLines = (rows, amountKey = "amount") => {
  if (!Array.isArray(rows) || rows.length === 0) return "—";
  return rows.map((row) => formatMoney(row?.[amountKey], row?.currency)).join("\n");
};

const sponsorText = (profile) => {
  if (!profile?.sponsor_name && !profile?.sponsor_uuid) return "—";
  return [profile.sponsor_name, profile.sponsor_uuid].filter(Boolean).join(" · ");
};

const paint = (cell, { font, fill, align } = {}) => {
  cell.font = { name: "Calibri", size: 11, color: { argb: INK }, ...font };
  if (fill) cell.fill = fill;
  cell.alignment = { vertical: "middle", wrapText: true, ...align };
  cell.border = border;
};

const addBanner = (ws, cols, { title, where, meta }) => {
  ws.mergeCells(1, 1, 1, cols);
  ws.mergeCells(2, 1, 2, cols);
  ws.mergeCells(3, 1, 3, cols);

  const titleCell = ws.getCell(1, 1);
  titleCell.value = title;
  titleCell.font = { name: "Calibri", size: 18, bold: true, color: { argb: WHITE } };
  titleCell.fill = solid(TEAL);
  titleCell.alignment = { vertical: "middle", indent: 1 };
  ws.getRow(1).height = 34;

  const whereCell = ws.getCell(2, 1);
  whereCell.value = where;
  whereCell.font = { name: "Calibri", size: 12, bold: true, color: { argb: TEAL_DARK } };
  whereCell.fill = solid(MINT);
  whereCell.alignment = { vertical: "middle", wrapText: true, indent: 1 };
  ws.getRow(2).height = 24;

  const metaCell = ws.getCell(3, 1);
  metaCell.value = meta;
  metaCell.font = { name: "Calibri", size: 11, color: { argb: SLATE } };
  metaCell.fill = solid(MINT_SOFT);
  metaCell.alignment = { vertical: "middle", wrapText: true, indent: 1 };
  ws.getRow(3).height = 22;

  ws.views = [{ state: "frozen", ySplit: 3 }];
  ws.pageSetup = {
    orientation: "landscape",
    fitToPage: true,
    fitToWidth: 1,
    fitToHeight: 0,
    paperSize: 9,
  };
  ws.pageSetup.printTitlesRow = "1:3";
  ws.headerFooter = {
    oddFooter: "&LDownline View&C&A&RPage &P of &N",
  };
  return 5;
};

const addSection = (ws, row, title, cols) => {
  ws.mergeCells(row, 1, row, cols);
  const cell = ws.getCell(row, 1);
  cell.value = title;
  cell.font = { name: "Calibri", size: 12, bold: true, color: { argb: TEAL_DARK } };
  cell.fill = solid(MINT);
  cell.alignment = { vertical: "middle", indent: 1 };
  ws.getRow(row).height = 22;
  return row + 1;
};

const addTable = (ws, row, headers, dataRows, { filter = false } = {}) => {
  const header = ws.getRow(row);
  headers.forEach((label, index) => {
    const cell = header.getCell(index + 1);
    cell.value = label;
    paint(cell, {
      font: { size: 10, bold: true, color: { argb: WHITE } },
      fill: solid(TEAL_DARK),
    });
  });
  header.height = 22;

  if (!dataRows.length) {
    const empty = ws.getRow(row + 1);
    const cell = empty.getCell(1);
    cell.value = "Is section mein koi row nahi hai.";
    paint(cell, { font: { italic: true, color: { argb: SLATE } } });
    ws.mergeCells(row + 1, 1, row + 1, headers.length);
    return row + 3;
  }

  dataRows.forEach((values, rowIndex) => {
    const dataRow = ws.getRow(row + 1 + rowIndex);
    let tall = 18;
    values.forEach((value, colIndex) => {
      const text = value == null || value === "" ? "—" : value;
      const cell = dataRow.getCell(colIndex + 1);
      cell.value = text;
      paint(cell, {
        font: { size: 10 },
        fill: rowIndex % 2 === 1 ? solid(ZEBRA) : undefined,
        align: { vertical: "top" },
      });
      const lines = String(text).split("\n").length;
      if (lines > 1) tall = Math.max(tall, lines * 16);
    });
    dataRow.height = tall;
  });

  if (filter) {
    ws.autoFilter = {
      from: { row, column: 1 },
      to: { row: row + dataRows.length, column: headers.length },
    };
  }
  return row + dataRows.length + 2;
};

const fitColumns = (ws, colCount) => {
  for (let col = 1; col <= colCount; col += 1) {
    let width = 16;
    ws.eachRow((row) => {
      const value = row.getCell(col).value;
      const text = value == null ? "" : String(value);
      text.split("\n").forEach((line) => {
        width = Math.max(width, Math.min(42, line.length + 3));
      });
    });
    ws.getColumn(col).width = width;
  }
};

const sheet = (workbook, name, cols, banner) => {
  const ws = workbook.addWorksheet(name, {
    properties: { defaultRowHeight: 18 },
  });
  const nextRow = addBanner(ws, cols, banner);
  return { ws, row: nextRow, cols };
};

const levelText = (level) =>
  level ? `Downline filter on the page: Level ${level} only` : "Downline filter on the page: All (Level 1 to 5)";

const personMeta = (profile, generatedAt) => {
  const when = generatedAt ? formatDate(generatedAt) : formatDate(new Date().toISOString());
  return `${dash(profile?.name)}  ·  UUID ${dash(profile?.uuid)}  ·  ${dash(profile?.email)}  ·  Report time ${when}`;
};

export async function downloadDownlineReport({ report, level }) {
  const user = report?.searched_user || {};
  const profile = user.profile || {};
  const summary = report?.summary || {};
  const commission = summary.commission_received_from_downline || user.commission_received_from_downline || {};
  const downline = Array.isArray(report?.downline) ? report.downline : [];
  const meta = personMeta(profile, report?.generated_at);
  const filterLine = levelText(level);
  const uuid = profile.uuid || profile.user_id || "user";

  const workbook = createWorkbook();
  workbook.creator = "Zillion Admin";
  workbook.created = new Date();

  const indexRows = [
    ["Index", "Yeh sheet", "Report ka index. Har row batati hai agla sheet page ke kis tab se aaya hai."],
    ["Overview", "Overview tab", "Profile, sponsor, level counts, wallet totals, staking totals, commission cards, own signup bonus."],
    ["Wallets", "Wallets tab", "Searched user ke saare wallets, phir Referral wallet (locked, available, released)."],
    ["Staking", "Staking tab", "Searched user ke staking packages. Coin alag rehte hain, USDT aur ZILN mix nahi."],
    ["Commission", "Commission tab", "Downline se is user ko kitna mila: staking, brokerage, referral, signup bonus, referral bonus."],
    ["Trades", "Trades tab", "Searched user ki apni spot trades. Khud ki trade par brokerage — hai."],
    ["Downline", "Downline tab", "Level 1 se 5 ke members. Page par jo level chip selected hai, wahi members."],
    ["DL Wallets", "Downline tab → row + → Wallets", "Har referral ke wallet balances."],
    ["DL Referral Wallet", "Downline tab → row + → Wallets → Referral wallet", "Har referral ka locked / available / released wallet."],
    ["DL Staking", "Downline tab → row + → Staking", "Har referral ke staking packages."],
    ["DL Staking Comm.", "Downline tab → row + → Commission → Staking commission", "Us referral se searched user ko mili staking commission, record by record."],
    ["DL Brokerage", "Downline tab → row + → Commission → Brokerage", "Us referral ki trades se mili brokerage. ZILN credit alag column mein hai."],
    ["DL Referral Comm.", "Downline tab → row + → Commission → Referral commission", "Signup bonus aur referral bonus records."],
    ["DL Trades", "Downline tab → row + → Trades", "Har referral ki spot trades, aur us trade se kitni brokerage mili."],
  ];

  {
    const { ws, row, cols } = sheet(workbook, "Index", 3, {
      title: "Downline View report",
      where: "Yeh file isi page ka data hai. Sheet ka naam aur 'Where on the page' column batate hain ye numbers screen ke kis tab se aaye hain.",
      meta: `${meta}  ·  ${filterLine}`,
    });
    addTable(ws, row, ["Sheet", "Where on the page", "Is sheet mein kya hai"], indexRows);
    fitColumns(ws, cols);
  }

  {
    const cols = 6;
    const { ws, row } = sheet(workbook, "Overview", cols, {
      title: "Overview",
      where: "Page tab: Overview. Upar wala profile, level counts, wallet totals, staking totals, teen commission cards, aur own signup bonus.",
      meta: `${meta}  ·  ${filterLine}`,
    });
    let cursor = addSection(ws, row, "Overview tab → Profile", cols);
    cursor = addTable(ws, cursor, ["Field", "Value"], [
      ["Name", dash(profile.name)],
      ["UUID", dash(profile.uuid)],
      ["Email", dash(profile.email)],
      ["Mobile", dash(profile.mobile)],
      ["Referral code", dash(profile.referral_code)],
      ["Status", dash(profile.account_status)],
      ["KYC", dash(profile.kyc_label)],
      ["Payout", dash(profile.payout_status)],
      ["Registered", formatDate(profile.registered_at)],
      ["Sponsor", sponsorText(profile)],
      ["Direct referrals", profile.total_direct_referrals ?? 0],
      ["Downline count", summary.downline_count ?? 0],
    ]);
    const levels = summary.by_level || {};
    cursor = addSection(ws, cursor, "Overview tab → Level counts", cols);
    cursor = addTable(
      ws,
      cursor,
      ["Level", "Members"],
      ["1", "2", "3", "4", "5"].map((item) => [`Level ${item}`, levels[item] ?? levels[Number(item)] ?? 0])
    );
    cursor = addSection(ws, cursor, "Overview tab → Wallet totals (coin wise, wallet type ka split Wallets sheet par hai)", cols);
    cursor = addTable(
      ws,
      cursor,
      ["Where on the page", "Coin", "Balance", "Locked"],
      (user.wallet_totals || []).map((item) => [
        "Overview tab → Wallet totals",
        dash(item.currency),
        formatMoney(item.balance, item.currency),
        formatMoney(item.locked_balance, item.currency),
      ])
    );
    cursor = addSection(ws, cursor, "Overview tab → Staking totals", cols);
    cursor = addTable(
      ws,
      cursor,
      ["Where on the page", "Coin", "Plans", "Invested", "Paid", "Remaining"],
      (user.staking_totals || []).map((item) => [
        "Overview tab → Staking totals",
        dash(item.currency),
        item.plans ?? 0,
        formatMoney(item.invested_amount, item.currency),
        formatMoney(item.paid_amount, item.currency),
        formatMoney(item.remaining_amount, item.currency),
      ])
    );
    cursor = addSection(ws, cursor, "Overview tab → Commission from downline", cols);
    const commissionBlocks = [
      ["Staking commission", commission.staking],
      ["Brokerage", commission.brokerage],
      ["Referral commission", commission.referral],
      ["Signup bonus", commission.signup_bonus],
      ["Referral bonus", commission.referral_bonus],
    ];
    cursor = addTable(ws, cursor, ["Where on the page", "Card", "Amount", "Records"], commissionBlocks.map(([label, block]) => [
      "Overview tab → Commission from downline",
      label,
      moneyLines(block?.total_by_currency),
      block?.count ?? 0,
    ]));
    const own = user.own_referral_rewards;
    cursor = addSection(ws, cursor, "Overview tab → Own signup bonus", cols);
    addTable(
      ws,
      cursor,
      ["Where on the page", "Type", "Level", "Amount", "Date"],
      (own?.records || []).map((item) => [
        "Overview tab → Own signup bonus",
        readableType(item.type),
        item.level ?? "—",
        formatMoney(item.amount, item.currency),
        formatDate(item.created_at),
      ])
    );
    fitColumns(ws, cols);
  }

  {
    const cols = 7;
    const { ws, row } = sheet(workbook, "Wallets", cols, {
      title: "Wallets",
      where: "Page tab: Wallets. Pehle normal wallets, neeche Referral wallet.",
      meta: `${meta}  ·  ${filterLine}`,
    });
    let cursor = addSection(ws, row, "Wallets tab → Balances", cols);
    cursor = addTable(
      ws,
      cursor,
      ["Where on the page", "Wallet", "Coin", "Balance", "Locked", "Bonus", "Status"],
      (user.wallets || []).map((item) => [
        "Wallets tab",
        dash(item.wallet_type),
        dash(item.currency),
        formatMoney(item.balance, item.currency),
        formatMoney(item.locked_balance, item.currency),
        formatMoney(item.bonus, item.currency),
        dash(item.status),
      ])
    );
    cursor = addSection(ws, cursor, "Wallets tab → Referral wallet", cols);
    addTable(
      ws,
      cursor,
      ["Where on the page", "Coin", "Total earned", "Locked", "Available", "Released", "Balance"],
      (user.referral_wallets || []).map((item) => [
        "Wallets tab → Referral wallet",
        dash(item.currency),
        formatMoney(item.total_earned, item.currency),
        formatMoney(item.locked_balance, item.currency),
        formatMoney(item.available_balance, item.currency),
        formatMoney(item.released_balance, item.currency),
        formatMoney(item.balance, item.currency),
      ])
    );
    fitColumns(ws, cols);
  }

  {
    const cols = 15;
    const { ws, row } = sheet(workbook, "Staking", cols, {
      title: "Staking",
      where: "Page tab: Staking. Searched user ke packages. Har row ka coin alag hai.",
      meta: `${meta}  ·  ${filterLine}`,
    });
    const cursor = addSection(ws, row, "Staking tab → Packages", cols);
    addTable(
      ws,
      cursor,
      ["Where on the page", "Package", "Coin", "Duration", "Return %", "Return type", "Invested", "Expected", "Paid", "Paid periods", "Remaining", "Status", "Payout", "Start", "End"],
      (user.staking || []).map((item) => [
        "Staking tab",
        dash(item.package_name),
        dash(item.currency),
        item.duration_days != null ? `${item.duration_days} days` : "—",
        item.return_percentage == null ? "—" : `${item.return_percentage}%`,
        dash(item.return_type),
        formatMoney(item.invested_amount, item.currency),
        formatMoney(item.expected_return, item.currency),
        formatMoney(item.paid_amount, item.currency),
        item.paid_days ?? "—",
        formatMoney(item.remaining_amount, item.currency),
        dash(item.status),
        dash(item.payout_status),
        formatDate(item.start_date),
        formatDate(item.end_date),
      ])
    );
    fitColumns(ws, cols);
  }

  {
    const cols = 4;
    const { ws, row } = sheet(workbook, "Commission", cols, {
      title: "Commission",
      where: "Page tab: Commission. Sirf totals. Record-by-record detail Downline expand sheets par hai (DL Staking Comm., DL Brokerage, DL Referral Comm.).",
      meta: `${meta}  ·  ${filterLine}`,
    });
    const block = user.commission_received_from_downline || commission;
    const cursor = addSection(ws, row, "Commission tab → Totals from downline", cols);
    addTable(ws, cursor, ["Where on the page", "Card", "Amount", "Records"], [
      ["Commission tab → Staking commission", "Staking commission", moneyLines(block?.staking?.total_by_currency), block?.staking?.count ?? 0],
      ["Commission tab → Brokerage", "Brokerage", moneyLines(block?.brokerage?.total_by_currency), block?.brokerage?.count ?? 0],
      ["Commission tab → Referral commission", "Referral commission", moneyLines(block?.referral?.total_by_currency), block?.referral?.count ?? 0],
      ["Commission tab → Referral commission → Signup bonus", "Signup bonus", moneyLines(block?.signup_bonus?.total_by_currency), block?.signup_bonus?.count ?? 0],
      ["Commission tab → Referral commission → Referral bonus", "Referral bonus", moneyLines(block?.referral_bonus?.total_by_currency), block?.referral_bonus?.count ?? 0],
    ]);
    fitColumns(ws, cols);
  }

  {
    const cols = 12;
    const { ws, row } = sheet(workbook, "Trades", cols, {
      title: "Trades",
      where: "Page tab: Trades. Searched user ki apni spot trades. Brokerage column — hai, kyunki khud ki trade se khud ko brokerage nahi milta.",
      meta: `${meta}  ·  ${filterLine}`,
    });
    const cursor = addSection(ws, row, "Trades tab → Own spot trades", cols);
    addTable(
      ws,
      cursor,
      ["Where on the page", "Order id", "Pair", "Side", "Type", "Status", "Qty", "Filled", "Price", "Fee", "Fills", "Brokerage"],
      (user.trades || []).map((item) => [
        "Trades tab",
        dash(item.order_id),
        dash(item.pair),
        dash(item.side),
        dash(item.order_type),
        dash(item.status),
        item.quantity ?? "—",
        item.filled ?? "—",
        item.price ?? "—",
        formatMoney(item.fee, item.fee_currency),
        item.fill_count ?? "—",
        "—",
      ])
    );
    fitColumns(ws, cols);
  }

  {
    const cols = 14;
    const { ws, row } = sheet(workbook, "Downline", cols, {
      title: "Downline",
      where: "Page tab: Downline. Table ki har row ek referral hai. Expand (+) ka detail agle sheets mein hai.",
      meta: `${meta}  ·  ${filterLine}`,
    });
    const cursor = addSection(ws, row, "Downline tab → Members", cols);
    addTable(
      ws,
      cursor,
      ["Where on the page", "Level", "Name", "UUID", "Email", "Mobile", "KYC", "Status", "Sponsor", "Staking", "Staking commission", "Brokerage", "Referral commission", "Trades"],
      downline.map((member) => {
        const person = member?.profile || {};
        const earned = member?.commission_to_searched_user;
        const stakingText = (member?.staking_totals || [])
          .map((item) => `${formatMoney(item.invested_amount, item.currency)} invested\n${formatMoney(item.paid_amount, item.currency)} paid`)
          .join("\n") || "—";
        return [
          "Downline tab",
          person.level ?? "—",
          dash(person.name),
          dash(person.uuid),
          dash(person.email),
          dash(person.mobile),
          dash(person.kyc_label),
          dash(person.account_status),
          dash(person.sponsor_name),
          stakingText,
          moneyLines(earned?.staking?.total_by_currency),
          moneyLines(earned?.brokerage?.total_by_currency),
          moneyLines(earned?.referral?.total_by_currency),
          member?.trade_count ?? 0,
        ];
      })
    );
    fitColumns(ws, cols);
  }

  const memberCols = (extra) => ["Where on the page", "Level", "Member", "Member UUID", ...extra];

  {
    const cols = 10;
    const { ws, row } = sheet(workbook, "DL Wallets", cols, {
      title: "Downline wallets",
      where: "Page: Downline tab → row ke + button → Wallets. Har referral ka wallet.",
      meta: `${meta}  ·  ${filterLine}`,
    });
    const cursor = addSection(ws, row, "Downline tab → expand → Wallets", cols);
    const rows = [];
    downline.forEach((member) => {
      const person = member?.profile || {};
      (member.wallets || []).forEach((item) => {
        rows.push([
          `Downline tab → ${dash(person.name)} → Wallets`,
          person.level ?? "—",
          dash(person.name),
          dash(person.uuid),
          dash(item.wallet_type),
          dash(item.currency),
          formatMoney(item.balance, item.currency),
          formatMoney(item.locked_balance, item.currency),
          formatMoney(item.bonus, item.currency),
          dash(item.status),
        ]);
      });
    });
    addTable(ws, cursor, memberCols(["Wallet", "Coin", "Balance", "Locked", "Bonus", "Status"]), rows);
    fitColumns(ws, cols);
  }

  {
    const cols = 10;
    const { ws, row } = sheet(workbook, "DL Referral Wallet", cols, {
      title: "Downline referral wallets",
      where: "Page: Downline tab → row ke + button → Wallets → Referral wallet.",
      meta: `${meta}  ·  ${filterLine}`,
    });
    const cursor = addSection(ws, row, "Downline tab → expand → Wallets → Referral wallet", cols);
    const rows = [];
    downline.forEach((member) => {
      const person = member?.profile || {};
      (member.referral_wallets || []).forEach((item) => {
        rows.push([
          `Downline tab → ${dash(person.name)} → Referral wallet`,
          person.level ?? "—",
          dash(person.name),
          dash(person.uuid),
          dash(item.currency),
          formatMoney(item.total_earned, item.currency),
          formatMoney(item.locked_balance, item.currency),
          formatMoney(item.available_balance, item.currency),
          formatMoney(item.released_balance, item.currency),
          formatMoney(item.balance, item.currency),
        ]);
      });
    });
    addTable(ws, cursor, memberCols(["Coin", "Total earned", "Locked", "Available", "Released", "Balance"]), rows);
    fitColumns(ws, cols);
  }

  {
    const cols = 18;
    const { ws, row } = sheet(workbook, "DL Staking", cols, {
      title: "Downline staking",
      where: "Page: Downline tab → row ke + button → Staking.",
      meta: `${meta}  ·  ${filterLine}`,
    });
    const cursor = addSection(ws, row, "Downline tab → expand → Staking", cols);
    const rows = [];
    downline.forEach((member) => {
      const person = member?.profile || {};
      (member.staking || []).forEach((item) => {
        rows.push([
          `Downline tab → ${dash(person.name)} → Staking`,
          person.level ?? "—",
          dash(person.name),
          dash(person.uuid),
          dash(item.package_name),
          dash(item.currency),
          item.duration_days != null ? `${item.duration_days} days` : "—",
          item.return_percentage == null ? "—" : `${item.return_percentage}%`,
          dash(item.return_type),
          formatMoney(item.invested_amount, item.currency),
          formatMoney(item.expected_return, item.currency),
          formatMoney(item.paid_amount, item.currency),
          item.paid_days ?? "—",
          formatMoney(item.remaining_amount, item.currency),
          dash(item.status),
          dash(item.payout_status),
          formatDate(item.start_date),
          formatDate(item.end_date),
        ]);
      });
    });
    addTable(
      ws,
      cursor,
      memberCols(["Package", "Coin", "Duration", "Return %", "Return type", "Invested", "Expected", "Paid", "Paid periods", "Remaining", "Status", "Payout", "Start", "End"]),
      rows
    );
    fitColumns(ws, cols);
  }

  {
    const cols = 13;
    const { ws, row } = sheet(workbook, "DL Staking Comm.", cols, {
      title: "Downline staking commission",
      where: "Page: Downline tab → row ke + button → Commission → Staking commission. Amount us coin mein hai jo column mein likha hai.",
      meta: `${meta}  ·  ${filterLine}`,
    });
    const cursor = addSection(ws, row, "Downline tab → expand → Commission → Staking commission", cols);
    const rows = [];
    downline.forEach((member) => {
      const person = member?.profile || {};
      const records = member?.commission_to_searched_user?.staking?.records || [];
      records.forEach((item) => {
        const source = item.source_amount == null ? "—" : formatMoney(item.source_amount, item.source_currency);
        rows.push([
          `Downline tab → ${dash(person.name)} → Commission → Staking commission`,
          person.level ?? "—",
          dash(person.name),
          dash(person.uuid),
          readableType(item.type),
          item.level ?? "—",
          formatMoney(item.amount, item.currency),
          item.credited_in_ziln && item.source_amount != null ? `from ${source}` : source,
          item.commission_percent == null ? "—" : `${item.commission_percent}%`,
          dash(item.package_currency),
          item.duration_days != null ? `${item.duration_days} days` : "—",
          formatMoney(item.invested_amount, item.package_currency),
          formatDate(item.credited_at),
        ]);
      });
    });
    addTable(
      ws,
      cursor,
      memberCols(["Type", "Commission level", "Amount", "Source", "Percent", "Package coin", "Duration", "Invested", "Date"]),
      rows
    );
    fitColumns(ws, cols);
  }

  {
    const cols = 12;
    const { ws, row } = sheet(workbook, "DL Brokerage", cols, {
      title: "Downline brokerage",
      where: "Page: Downline tab → row ke + button → Commission → Brokerage. Credit column batata hai amount ZILN hai ya usi coin mein.",
      meta: `${meta}  ·  ${filterLine}`,
    });
    const cursor = addSection(ws, row, "Downline tab → expand → Commission → Brokerage", cols);
    const rows = [];
    downline.forEach((member) => {
      const person = member?.profile || {};
      const trades = member?.trades || [];
      const records = member?.commission_to_searched_user?.brokerage?.records || [];
      records.forEach((item) => {
        const order = trades.find((trade) => trade.order_db_id && trade.order_db_id === item.order_db_id);
        const source = item.source_amount == null ? "—" : formatMoney(item.source_amount, item.source_currency);
        rows.push([
          `Downline tab → ${dash(person.name)} → Commission → Brokerage`,
          person.level ?? "—",
          dash(person.name),
          dash(person.uuid),
          dash(order?.order_id),
          formatMoney(item.amount, item.currency),
          item.credited_in_ziln && item.source_amount != null ? `from ${source}` : source,
          item.credited_in_ziln ? "ZILN" : dash(item.currency),
          item.commission_percent == null ? "—" : `${item.commission_percent}%`,
          item.trade_fee ?? "—",
          formatDate(item.created_at),
          item.credited_in_ziln ? "Amount ZILN mein hai" : "Purani entry: amount fee wali coin mein hai",
        ]);
      });
    });
    addTable(
      ws,
      cursor,
      memberCols(["Order", "Amount", "Source", "Credit", "Percent", "Trade fee", "Date", "Note"]),
      rows
    );
    fitColumns(ws, cols);
  }

  {
    const cols = 8;
    const { ws, row } = sheet(workbook, "DL Referral Comm.", cols, {
      title: "Downline referral commission",
      where: "Page: Downline tab → row ke + button → Commission → Referral commission.",
      meta: `${meta}  ·  ${filterLine}`,
    });
    const cursor = addSection(ws, row, "Downline tab → expand → Commission → Referral commission", cols);
    const rows = [];
    downline.forEach((member) => {
      const person = member?.profile || {};
      const referral = member?.commission_to_searched_user?.referral;
      (referral?.records || []).forEach((item) => {
        rows.push([
          `Downline tab → ${dash(person.name)} → Commission → Referral commission`,
          person.level ?? "—",
          dash(person.name),
          dash(person.uuid),
          readableType(item.type),
          item.level ?? "—",
          formatMoney(item.amount, item.currency),
          formatDate(item.created_at),
        ]);
      });
    });
    addTable(ws, cursor, memberCols(["Type", "Commission level", "Amount", "Date"]), rows);
    fitColumns(ws, cols);
  }

  {
    const cols = 16;
    const { ws, row } = sheet(workbook, "DL Trades", cols, {
      title: "Downline trades",
      where: "Page: Downline tab → row ke + button → Trades. Brokerage column batati hai is trade se searched user ko kitna mila.",
      meta: `${meta}  ·  ${filterLine}`,
    });
    const cursor = addSection(ws, row, "Downline tab → expand → Trades", cols);
    const rows = [];
    downline.forEach((member) => {
      const person = member?.profile || {};
      (member.trades || []).forEach((item) => {
        rows.push([
          `Downline tab → ${dash(person.name)} → Trades`,
          person.level ?? "—",
          dash(person.name),
          dash(person.uuid),
          dash(item.order_id),
          dash(item.pair),
          dash(item.side),
          dash(item.order_type),
          dash(item.status),
          item.quantity ?? "—",
          item.filled ?? "—",
          item.price ?? "—",
          formatMoney(item.fee, item.fee_currency),
          item.fill_count ?? "—",
          formatMoney(item.brokerage_to_searched_user, item.brokerage_currency),
          formatDate(item.created_at),
        ]);
      });
    });
    addTable(
      ws,
      cursor,
      memberCols(["Order id", "Pair", "Side", "Type", "Status", "Qty", "Filled", "Price", "Fee", "Fills", "Brokerage to searched user", "Time"]),
      rows
    );
    fitColumns(ws, cols);
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `downline-view-${uuid}.xlsx`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
